import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "app/NotificationProvider";
import { AnalyticsEvents, analytics } from "common/analytics";
import { useTranslation } from "common/locales";
import { EventType, Field, useAnimalGroup, useFields } from "model";
import React, { useEffect, useMemo, useState } from "react";
import {
  createOneReading,
  deleteAllTilesForField,
  invalidateFieldData,
} from "services";
import { moveToShed } from "services/readingOperations";
import { useUIStore } from "stores";
import { moveAnimalsIn } from "./moveAnimalsIn";

type Mode = "fieldToField" | "fieldToShed" | "shedToField";

export default function UpdateBiomassAfterMoveInDialog() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();

  const moveAnimalsDialogDetails = useUIStore(
    (state) => state.moveAnimalsDialogDetails
  );
  const closeMoveOutEventDialog = useUIStore(
    (state) => state.closeMoveOutEventDialog
  );

  const initialFromField = moveAnimalsDialogDetails.currentFieldWithAnimals;
  const toField = moveAnimalsDialogDetails.newFieldWithAnimals;
  const isMoveToShed = moveAnimalsDialogDetails.moveToShed ?? false;

  // Derive mode
  const mode: Mode = isMoveToShed
    ? "fieldToShed"
    : initialFromField && toField
    ? "fieldToField"
    : "shedToField";

  // For fieldToField and shedToField, use destination's animal group
  // For fieldToShed, use source's animal group
  const animalGroupId =
    mode === "fieldToShed"
      ? initialFromField?.animalGroup
      : toField?.animalGroup;
  const { data: allFields = [] } = useFields();
  const { data: animalGroup } = useAnimalGroup(animalGroupId);

  const postGrazingTarget = animalGroup?.postGrazingTarget;

  // Fields that belong to the same animal group as the destination (for from-field autocomplete)
  const groupFields = useMemo(
    () =>
      toField?.animalGroup
        ? allFields.filter(
            (f) =>
              f.animalGroup === toField.animalGroup && f.guid !== toField.guid
          )
        : [],
    [allFields, toField]
  );

  const [moveDate, setMoveDate] = useState<Date | null>(new Date());
  const [dateError, setDateError] = useState(false);
  const [fromField, setFromField] = useState<Field | null>(
    initialFromField ?? null
  );
  const [postGrazingCover, setPostGrazingCover] = useState<number | "">(
    initialFromField?.dryMatterNow ?? ""
  );
  const [preGrazingCover, setPreGrazingCover] = useState<number | "">(
    toField?.dryMatterNow ?? ""
  );
  const [saving, setSaving] = useState(false);

  // Sync state when dialog opens with new details
  useEffect(() => {
    if (moveAnimalsDialogDetails.open) {
      setFromField(initialFromField ?? null);
      setPostGrazingCover(initialFromField?.dryMatterNow ?? "");
      setPreGrazingCover(toField?.dryMatterNow ?? "");
      setMoveDate(new Date());
      setDateError(false);
      setSaving(false);
    }
  }, [moveAnimalsDialogDetails.open, initialFromField, toField]);

  // Update post-grazing cover default when from field changes
  useEffect(() => {
    if (fromField) {
      setPostGrazingCover(fromField.dryMatterNow ?? "");
    }
  }, [fromField]);

  function handleClose() {
    closeMoveOutEventDialog();
  }

  async function handleSave() {
    if (dateError || !moveDate) return;
    if (mode !== "fieldToShed" && !toField) return;

    setSaving(true);
    analytics.track(AnalyticsEvents.ANIMAL_MOVE_STARTED);

    try {
      const dateIso = moveDate.toISOString();

      // Post-grazing reading on source field (fieldToField and fieldToShed)
      if (
        (mode === "fieldToField" || mode === "fieldToShed") &&
        fromField &&
        postGrazingCover !== ""
      ) {
        await createOneReading({
          guid: "new",
          manualDryMatterInput: {
            dryMatterValue: postGrazingCover,
            field: fromField.guid,
            dateTimeMeasurementStart: dateIso,
            dateTimeMeasurementEnd: dateIso,
            event: { eventType: EventType.None },
            kind: "user",
            verificationStatus: "Accepted",
          },
        });
        await deleteAllTilesForField(fromField.guid);
        invalidateFieldData(fromField.guid);
      }

      // Pre-grazing reading on destination field (fieldToField and shedToField)
      if (
        (mode === "fieldToField" || mode === "shedToField") &&
        toField &&
        preGrazingCover !== ""
      ) {
        await createOneReading({
          guid: "new",
          manualDryMatterInput: {
            dryMatterValue: preGrazingCover,
            field: toField.guid,
            dateTimeMeasurementStart: dateIso,
            dateTimeMeasurementEnd: dateIso,
            event: { eventType: EventType.None },
            kind: "user",
            verificationStatus: "Accepted",
          },
        });
        await deleteAllTilesForField(toField.guid);
        invalidateFieldData(toField.guid);
      }

      // Execute the move
      if (mode === "fieldToShed" && fromField) {
        await moveToShed(fromField.guid, moveDate, queryClient);
        notify(t("animalGroup.moveToShed.success"));
      } else if (toField) {
        await moveAnimalsIn(
          toField,
          fromField ?? undefined,
          moveDate,
          notify,
          t
        );
      }

      closeMoveOutEventDialog();
    } catch (e) {
      console.error(e);
      if (mode === "fieldToShed") {
        notify(t("animalGroup.moveToShed.error"), "error");
      }
      setSaving(false);
    }
  }

  // Dialog title
  const dialogTitle =
    mode === "fieldToShed"
      ? t("moveAnimals.toShed.title")
      : mode === "shedToField"
      ? `${t("moveAnimals.titlePrefix")} ${animalGroup?.name ?? ""}`
      : `${t("moveAnimals.titlePrefix")} ${animalGroup?.name ?? ""}`;

  const showFromField = mode === "fieldToField";
  const showPostGrazingCover =
    mode === "fieldToField" || mode === "fieldToShed";
  const showToField = mode === "fieldToField" || mode === "shedToField";
  const showPreGrazingCover = mode === "fieldToField" || mode === "shedToField";

  return (
    <Dialog
      open={moveAnimalsDialogDetails.open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        {mode === "fieldToShed" ? (
          dialogTitle
        ) : (
          <>
            <Box
              component="span"
              sx={{ color: "text.secondary", fontWeight: 400 }}
            >
              {t("moveAnimals.titlePrefix")}{" "}
            </Box>
            {animalGroup?.name ?? ""}
          </>
        )}
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          pt: "8px !important",
        }}
      >
        {/* Date */}
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("moveAnimals.date.label")}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              format="MMMM do HH:mm"
              ampm={false}
              value={moveDate}
              onChange={(value: Date | null) => {
                setDateError(false);
                setMoveDate(value);
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  error: dateError,
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* From field (fieldToField only) */}
        {showFromField && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("moveAnimals.from.label")}
            </Typography>
            <Autocomplete
              options={groupFields}
              getOptionLabel={(field) => field.name}
              value={fromField}
              onChange={(_, newValue) => setFromField(newValue)}
              size="small"
              noOptionsText={t("moveAnimals.noFields")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" />
              )}
            />
          </Box>
        )}

        {/* Post-grazing cover (fieldToField and fieldToShed) */}
        {showPostGrazingCover && (
          <Box>
            {mode === "fieldToShed" && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {initialFromField?.name}
              </Typography>
            )}
            <TextField
              label={t("moveAnimals.postGrazingCover.label")}
              variant="outlined"
              size="small"
              fullWidth
              type="number"
              value={postGrazingCover}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPostGrazingCover(
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              disabled={mode === "fieldToField" && !fromField}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end" sx={{ gap: 0.5 }}>
                      {t("addCovers.single.unit")}
                      {postGrazingTarget != null &&
                        (mode === "fieldToShed" || fromField) && (
                          <Button
                            variant="outlined"
                            onClick={() =>
                              setPostGrazingCover(postGrazingTarget)
                            }
                            sx={{
                              minWidth: "auto",
                              fontSize: "12px",
                              fontWeight: 500,
                              textTransform: "none",
                              borderColor: "#2E7831",
                              color: "#2E7831",
                              borderRadius: "8px",
                              px: 1,
                              py: 0.25,
                              whiteSpace: "nowrap",
                              "&:hover": {
                                borderColor: "#1C4321",
                                backgroundColor: "#F7FCF1",
                              },
                            }}
                          >
                            {t("moveAnimals.useTarget", {
                              value: postGrazingTarget,
                            })}
                          </Button>
                        )}
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        )}

        {/* To field (fieldToField and shedToField) */}
        {showToField && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {mode === "shedToField"
                ? t("moveAnimals.fromShed.label")
                : t("moveAnimals.to.label")}
            </Typography>
            <Typography variant="body1" fontWeight={500} sx={{ mb: 1.5 }}>
              {toField?.name}
            </Typography>
          </Box>
        )}

        {/* Pre-grazing cover (fieldToField and shedToField) */}
        {showPreGrazingCover && (
          <Box>
            <TextField
              label={t("moveAnimals.preGrazingCover.label")}
              variant="outlined"
              size="small"
              fullWidth
              type="number"
              value={preGrazingCover}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPreGrazingCover(
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {t("addCovers.single.unit")}
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="secondary">
          {t("moveAnimals.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={dateError || !moveDate || saving}
        >
          {t("moveAnimals.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
